import { useMemo } from 'react';
import classNames from 'classnames';

import { Button } from '../../general-ui/button/button';
import { EIcon } from '../../general-ui/icon/icon';
import { useParams } from 'react-router';
import { ParamsProps } from '../live-event';
import { BrandliveEvent } from '../../../types/working-model';
import { MarkdownItem } from '../../general-ui/typography-item/typography-item';
import { useTypedSelector } from '../../../store/reducers/use-typed-selector';
import { useTranslate } from '../../../i18n/useTranslationModules';
import { OptionalComponent } from '../../../utils/optional-component';

enum RegFailureReasons {
	passcode = 'invalid-passcode',
	domain = 'invalid-email-domain',
	missingRequired = 'missing-required-field',
	missingPasscodeLists = 'no-matching-passcode-lists',
	notFoundInPasscodeLists = 'not-found-passcode-lists',
	registrationClosed = 'registration-closed',
	badWords = 'bad-words-detected'
}

export interface RegistrationFooterProps {
	isNextDisabled?: boolean;
	isFirstStep: boolean;
	isFinalStep: boolean;
	useCustomLogin?: boolean;
	template?: string;
	onPrevStepClick: (e: any) => void;
	waiting: boolean;
	error?: string;
	notSkippable?: boolean;
	eventBundle?: BrandliveEvent | null;
	renderRecaptcha?: () => JSX.Element;
}
export const RegistrationFooter = ({
	isNextDisabled,
	isFirstStep,
	isFinalStep,
	useCustomLogin,
	template,
	onPrevStepClick,
	waiting,
	error = '',
	notSkippable,
	eventBundle,
	renderRecaptcha
}: RegistrationFooterProps): JSX.Element => {
	const { t } = useTranslate("homepage");
	const { language } = useParams() as ParamsProps;

	const isLiveClosed = useTypedSelector(state => !!state.LiveEventReducer?.eventBundle?.registration_settings?.isClosed);
	const isWorkingClosed = useTypedSelector(state => !!state.CreateEventReducer?.workingEvent?.registration_settings?.isClosed);
	const isRegistrationClosed = isLiveClosed || isWorkingClosed;

	const regErrorMessage = useMemo(() => {
		if (!error) return '';
		let errorMessage;

		switch (error) {
			case RegFailureReasons.domain: {
				errorMessage = t("homepage:registration.domain");
				break;
			}
			// this should never fire because submission is (should be) blocked until required fields are completed
			case RegFailureReasons.missingRequired: {
				errorMessage = t("homepage:registration.missingRequired");
				break;
			}
			case RegFailureReasons.notFoundInPasscodeLists: {
				if (eventBundle?.registration_settings?.customRejectedPasscodeMessage?.base) {
					return (
						<MarkdownItem className="registration-error custom-failed-passcode" allowedElements={['a', 'p']}>
							{eventBundle?.registration_settings?.customRejectedPasscodeMessage?.[language] as string ?? eventBundle?.registration_settings?.customRejectedPasscodeMessage?.base}
						</MarkdownItem>
					);
				}
				errorMessage = t("homepage:registration.notFoundInPasscodeLists");
				break;
			}
			case RegFailureReasons.passcode: {
				if (eventBundle?.registration_settings?.customRejectedPasscodeMessage?.base) {
					return (
						<MarkdownItem className="registration-error custom-failed-passcode" allowedElements={['a', 'p']}>
							{eventBundle?.registration_settings?.customRejectedPasscodeMessage?.[language] as string ?? eventBundle?.registration_settings?.customRejectedPasscodeMessage?.base}
						</MarkdownItem>
					);
				}
				errorMessage = t("homepage:registration.passcode", eventBundle?.registration_settings?.customPasscodeLabel?.[language] as string || t(`registration.registration_fields.10`, 'Passcode')); ////RegFieldsEnum.passcode - typescript will yell at you if you use the enum here
				break;
			}
			case RegFailureReasons.registrationClosed: {
				errorMessage = t("homepage:registration.Registration is closed");
				break;
			}
			case RegFailureReasons.badWords: {
				errorMessage = t("homepage:registration.badWords");
				break;
			}
			default: {
				errorMessage = t("homepage:registration.error");
			}
		}
		return <p className="registration-error">{errorMessage}</p>;
	}, [error, eventBundle?.registration_settings?.customPasscodeLabel, eventBundle?.registration_settings?.customRejectedPasscodeMessage, language, t]);

	if (useCustomLogin && isFirstStep) return <></>;
	if (isFirstStep)
		return (
			<div className="registration-actions primary">
				{error && <p className="registration-error" role="alert">{regErrorMessage}</p>}
				{renderRecaptcha?.()}
				<Button
					classButton="primary full-width"
					template={template}
					typeBtn="submit"
					isDisabled={isNextDisabled || waiting || isRegistrationClosed}
				>
					{isRegistrationClosed ? t("homepage:registration.Registration is closed") : t("Next")}
				</Button>
			</div>
		);
	else if (isFinalStep)
		return (
			<div className="registration-actions secondary">
				{error && <p className="registration-error" role="alert">{regErrorMessage}</p>}
				<OptionalComponent display={!useCustomLogin}>
					<Button
						isRound={true}
						classButton={classNames('registration-button secondary')}
						template={template}
						onClick={onPrevStepClick}
						typeBtn="button"
					>
						<EIcon name="keyboard-arrow-left" />
					</Button>
				</OptionalComponent>

				<div>
					{!notSkippable && (<Button
						classButton={classNames('registration-button secondary')}
						template={template}
						typeBtn="submit"
						isDisabled={waiting || isRegistrationClosed}
					>
						{isRegistrationClosed ? t("homepage:registration.Registration is closed") : t("Skip")}
					</Button>)}
					<Button
						classButton={classNames('registration-button primary')}
						template={template}
						typeBtn="submit"
						isDisabled={isNextDisabled || waiting || isRegistrationClosed}
					>
						{isRegistrationClosed ? t("homepage:registration.Registration is closed") : t("Submit")}
					</Button>
				</div>
			</div>
		);
	return (
		<div className="registration-actions secondary">
			{error && <p className="registration-error" role="alert">{regErrorMessage}</p>}
			<OptionalComponent display={!useCustomLogin}>
				<Button
					isRound={true}
					classButton={classNames('registration-button secondary')}
					template={template}
					onClick={onPrevStepClick}
				>
					<EIcon name="keyboard-arrow-left" />
				</Button>
			</OptionalComponent>
			<div>
				{!notSkippable && (<Button
					classButton={classNames('registration-button secondary')}
					template={template}
					isDisabled={waiting}
					typeBtn="submit"
				>
					{t("Skip")}
				</Button>)}
				<Button
					classButton={classNames('registration-button primary')}
					template={template}
					isDisabled={isNextDisabled || waiting}
					typeBtn="submit"
				>
					{t("homepage:Next")}
				</Button>
			</div>
		</div>
	);
};
